import type { ReactNode } from 'react'
import { AlertTriangle, Info, Workflow as WorkflowIcon } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import type { WorkflowView } from '../types'
import { useWorkflowViews } from '../hooks/useWorkflowViews'
import { TriggerChip, getTriggerDescription } from './TriggerChip'
import { WorkflowFacts } from './WorkflowFacts'
import { DutyLink, SessionsLink } from './WorkflowLinks'

// Both listings return every workflow, so the detail view resolves its record
// from those same cached queries by slug rather than refetching one by id.
export function WorkflowDetail({ slug }: { slug: string }) {
  const {
    data,
    isLoading,
    isError,
    isCatalogueUnavailable,
    hasNoOrg,
    refetch,
  } = useWorkflowViews()

  // No org means nothing to fetch — and a skeleton that would never resolve,
  // since the catalogue query stays disabled rather than failing.
  if (hasNoOrg) {
    return (
      <DetailMessage>
        <p className="text-muted-foreground">
          Select an organization to see its workflows.
        </p>
      </DetailMessage>
    )
  }

  if (isLoading) {
    return <WorkflowDetailSkeleton />
  }

  if (isError) {
    return (
      <DetailMessage>
        <p className="text-muted-foreground">
          Couldn’t load workflows. Please try again.
        </p>
        <Button size="sm" variant="outline" onClick={() => refetch()}>
          Retry
        </Button>
      </DetailMessage>
    )
  }

  const workflow = data.find((w) => w.slug === slug)

  // Checked before the not-found branch, which would otherwise blame nova for
  // removing a workflow that is merely unlistable right now.
  if (!workflow && isCatalogueUnavailable) {
    return (
      <DetailMessage>
        <p className="text-muted-foreground">
          The workflow catalogue is unavailable, so{' '}
          <span className="font-mono">{slug}</span> can’t be loaded.
        </p>
        <p className="max-w-md text-sm text-muted-foreground">
          Workflow definitions come from Nebula. This doesn’t mean the workflow
          is gone.
        </p>
        <Button size="sm" variant="outline" onClick={() => refetch()}>
          Retry
        </Button>
      </DetailMessage>
    )
  }

  if (!workflow) {
    return (
      <DetailMessage>
        <p className="text-muted-foreground">
          No workflow found for <span className="font-mono">{slug}</span>.
        </p>
        <p className="max-w-md text-sm text-muted-foreground">
          This organization’s workflow catalogue comes from Nebula — a workflow
          removed from the nova repo stops appearing here.
        </p>
      </DetailMessage>
    )
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-6 py-6">
        <WorkflowHeader workflow={workflow} />

        {workflow.deprecated && <DeprecatedNotice />}

        {workflow.description && (
          <div className="flex gap-2.5 rounded-lg border border-indigo-100 bg-indigo-50/50 px-4 py-3 dark:border-indigo-900 dark:bg-indigo-950/20">
            <Info className="mt-0.5 size-4 shrink-0 text-indigo-500 dark:text-indigo-400" />
            <p className="text-sm leading-relaxed text-muted-foreground">
              {workflow.description}
            </p>
          </div>
        )}

        <Section title="Trigger">
          <Card className="flex-row items-center gap-3 px-5 py-4">
            {workflow.trigger ? (
              <>
                <TriggerChip trigger={workflow.trigger} showLabel={false} />
                <p className="text-sm text-muted-foreground">
                  {getTriggerDescription(workflow.trigger)}
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground italic">
                No trigger recorded — Nebula resolves triggers per duty, so this
                workflow has none until it is registered in Hoku.
              </p>
            )}
          </Card>
        </Section>

        <Section title="Duties">
          <Card className="px-5 py-4">
            {workflow.duties.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">
                Not linked to any duty.
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {workflow.duties.map((duty) => (
                  <DutyLink key={duty.id} duty={duty} />
                ))}
              </div>
            )}
          </Card>
        </Section>

        <Section title="Sessions">
          <Card className="flex-row items-center justify-between gap-3 px-5 py-4">
            <SessionsLink slug={workflow.slug} />
            {/* The link stands either way — sessions are found by slug, with or
                without a db row — but the count needs one, so an unregistered
                workflow says nothing rather than "0 sessions". */}
            <span className="text-sm text-muted-foreground">
              {workflow.sessionCount === null
                ? 'Not counted without a Hoku row'
                : `${workflow.sessionCount.toLocaleString('en-US')} ${
                    workflow.sessionCount === 1 ? 'session' : 'sessions'
                  }`}
            </span>
          </Card>
        </Section>

        <Section title="Facts">
          <Card className="px-5 py-4">
            <WorkflowFacts
              facts={workflow.facts}
              rawText={workflow.factsText}
            />
          </Card>
        </Section>
      </div>
    </div>
  )
}

function WorkflowHeader({ workflow }: { workflow: WorkflowView }) {
  return (
    <div className="flex items-start gap-4">
      <WorkflowIcon
        size={26}
        strokeWidth={1.5}
        className="mt-1 shrink-0 text-primary"
      />
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <h1 className="text-xl font-semibold text-primary-high-contrast">
          {workflow.title}
        </h1>
        {workflow.deprecated && <Badge variant="destructive">Deprecated</Badge>}
        {/* Switched off by every duty that holds it — the duty list below marks
            which ones. */}
        {!workflow.active && <Badge variant="outline">Inactive</Badge>}
        {/* Nebula can run it, but Hoku has no row for it — so the duty links and
            session count below are empty for a reason worth naming. */}
        {workflow.unregistered && (
          <Badge variant="outline">Not registered in Hoku</Badge>
        )}
      </div>
    </div>
  )
}

// The page still shows the trigger, duties and facts of a deprecated workflow —
// they are the record of how it used to run — so name the one thing that
// changed: no new sessions can start. The sessions it already has stay linked.
function DeprecatedNotice() {
  return (
    <div className="flex gap-2.5 rounded-lg border border-error-ui-border bg-error-ui-bg px-4 py-3">
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-error-solid" />
      <p className="text-sm leading-relaxed text-muted-foreground">
        This workflow is deprecated — it has been retired in the nova repo and
        can no longer be run. Its existing sessions are still available below.
      </p>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold text-primary-high-contrast">
        {title}
      </h2>
      {children}
    </section>
  )
}

function DetailMessage({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 py-12 text-center">
      {children}
    </div>
  )
}

function WorkflowDetailSkeleton() {
  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-6 py-6">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-3 w-1/4" />
        <Skeleton className="h-3 w-3/4" />
      </div>
      <Skeleton className="h-40 w-full" />
    </div>
  )
}
